import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Store,
  Package,
  ShoppingCart,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  TrendingUp,
  FileText,
  Search,
  Plus,
  Minus,
  Trash2,
  Calendar,
  Receipt,
  Truck,
  LogOut,
  Menu,
  X,
  User,
  CreditCard,
  Banknote,
  Smartphone,
  AlertCircle,
  RefreshCw,
  Filter,
  Download
} from "lucide-react";

const LABELS = {
  dashboard: { en: "Dashboard", hi: "डैशबोर्ड" },
  stock: { en: "Stock", hi: "स्टॉक" },
  pendingOrders: { en: "Pending Orders", hi: "लंबित ऑर्डर" },
  orderStock: { en: "Order Stock", hi: "स्टॉक ऑर्डर" },
  settlement: { en: "Settlement", hi: "सेटलमेंट" },
  newSale: { en: "New Bill", hi: "नई बिल" },
  dailyReport: { en: "Daily Report", hi: "दैनिक रिपोर्ट" },
  salesHistory: { en: "Bill History", hi: "बिल इतिहास" },
  directSale: { en: "Direct Sale", hi: "सीधी बिक्री" },
  memberSale: { en: "Member Sale", hi: "सदस्य बिक्री" },
  totalSales: { en: "Total Sales", hi: "कुल बिक्री" },
  totalCommission: { en: "Total Commission", hi: "कुल कमीशन" },
  confirm: { en: "Confirm", hi: "पुष्टि करें" },
  search: { en: "Search", hi: "खोजें" },
  logout: { en: "Logout", hi: "लॉगआउट" },
  quantity: { en: "Quantity", hi: "मात्रा" },
  price: { en: "Price", hi: "कीमत" },
  total: { en: "Total", hi: "कुल" },
  cash: { en: "Cash", hi: "नकद" },
  upi: { en: "UPI", hi: "यूपीआई" },
  card: { en: "Card", hi: "कार्ड" },
  credit: { en: "Credit", hi: "क्रेडिट" },
  wallet: { en: "Wallet", hi: "वॉलेट" },
  paymentMode: { en: "Payment Mode", hi: "भुगतान मोड" },
  memberId: { en: "Member ID", hi: "सदस्य आईडी" },
  customerName: { en: "Customer Name", hi: "ग्राहक का नाम" },
  phone: { en: "Phone", hi: "फोन" },
  completeSale: { en: "Generate Bill", hi: "बिल बनाएं" },
  availableCredit: { en: "Available Credit", hi: "उपलब्ध क्रेडिट" },
  verifyMember: { en: "Verify Member", hi: "सदस्य सत्यापित करें" },
  selectProducts: { en: "Select Products", hi: "उत्पाद चुनें" },
  noStock: { en: "No Stock", hi: "स्टॉक नहीं" },
  today: { en: "Today", hi: "आज" }
};

const L = (key, lang) => LABELS[key]?.[lang] || LABELS[key]?.en || key;

export default function FranchiseDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [lang, setLang] = useState("en");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [franchise, setFranchise] = useState(null);
  const [stock, setStock] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [sales, setSales] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  
  // New sale states
  const [saleType, setSaleType] = useState("direct");
  const [memberId, setMemberId] = useState("");
  const [memberInfo, setMemberInfo] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [cartItems, setCartItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [billDate, setBillDate] = useState(new Date().toISOString().split("T")[0]);
  const [saleNotes, setSaleNotes] = useState("");
  // Create-new-member (franchise) states
  const [newMemberOpen, setNewMemberOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", phone: "", plan_type: "D", sponsor_id: "" });
  const [creatingMember, setCreatingMember] = useState(false);
  const [newMemberResult, setNewMemberResult] = useState(null);
  
  // History filters
  const [historyFilter, setHistoryFilter] = useState({ date: "", search: "", type: "" });
  const [selectedBill, setSelectedBill] = useState(null);

  // Stock order (franchise -> head office) states
  const MIN_ORDER_VALUE = 5000;
  const [catalog, setCatalog] = useState([]);
  const [orderCart, setOrderCart] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [myOrders, setMyOrders] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);

  // Settlement states
  const [settlement, setSettlement] = useState(null);
  const [settlementHistory, setSettlementHistory] = useState([]);

  useEffect(() => {
    if (!user || user.role !== "franchise") {
      navigate("/login");
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, stockRes, transfersRes, salesRes, reportRes] = await Promise.all([
        axios.get(`${API}/franchise/profile`, { headers }),
        axios.get(`${API}/franchise/stock`, { headers }),
        axios.get(`${API}/franchise/transfers?status=pending`, { headers }),
        axios.get(`${API}/franchise/sales`, { headers }),
        axios.get(`${API}/franchise/daily-report`, { headers })
      ]);
      setFranchise(profileRes.data);
      setStock(stockRes.data || []);
      setPendingTransfers(transfersRes.data || []);
      setSales(salesRes.data || []);
      setDailyReport(reportRes.data);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) { logout(); navigate("/login"); }
    } finally { setLoading(false); }  };

  // ---- Stock Order (Franchise -> Head Office) ----
  const fetchOrderData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [catRes, ordersRes] = await Promise.all([
        axios.get(`${API}/products?include_internal=true`),
        axios.get(`${API}/franchise/stock-orders`, { headers })
      ]);
      setCatalog(catRes.data || []);
      setMyOrders(ordersRes.data || []);
    } catch (err) { console.error(err); }
  };

  const addToOrder = (product) => {
    const exists = orderCart.find(c => c.product_id === product.id);
    if (exists) {
      setOrderCart(orderCart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setOrderCart([...orderCart, { product_id: product.id, product_name: product.name, unit_price: product.price, quantity: 1 }]);
    }
  };

  const updateOrderQty = (pid, qty) => {
    if (qty <= 0) { setOrderCart(orderCart.filter(c => c.product_id !== pid)); return; }
    setOrderCart(orderCart.map(c => c.product_id === pid ? { ...c, quantity: qty } : c));
  };

  const getOrderTotal = () => orderCart.reduce((s, c) => s + c.unit_price * c.quantity, 0);

  const placeOrder = async () => {
    const total = getOrderTotal();
    if (total < MIN_ORDER_VALUE) { toast.error(`Minimum order value is Rs ${MIN_ORDER_VALUE.toLocaleString()}`); return; }
    setPlacingOrder(true);
    try {
      await axios.post(`${API}/franchise/stock-orders`, {
        products: orderCart.map(c => ({ product_id: c.product_id, product_name: c.product_name, quantity: c.quantity })),
        notes: orderNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Stock order placed successfully!");
      setOrderCart([]); setOrderNotes("");
      fetchOrderData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    } finally { setPlacingOrder(false); }
  };

  useEffect(() => {
    if (activeTab === "orderStock") fetchOrderData();
    if (activeTab === "settlement") fetchSettlement();
     
  }, [activeTab]);

  const fetchSettlement = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sumRes, histRes] = await Promise.all([
        axios.get(`${API}/franchise/settlement-summary`, { headers }),
        axios.get(`${API}/franchise/settlements`, { headers })
      ]);
      setSettlement(sumRes.data);
      setSettlementHistory(histRes.data || []);
    } catch (err) { console.error(err); }
  };

  const verifyMember = async () => {
    if (!memberId) return;
    try {
      const res = await axios.get(`${API}/franchise/verify-member/${memberId.toUpperCase()}`, { headers: { Authorization: `Bearer ${token}` } });
      setMemberInfo(res.data);
      toast.success(`Member verified: ${res.data.name}`);
    } catch (err) { toast.error("Member not found"); setMemberInfo(null); }
  };

  const createMember = async () => {
    if (!newMember.name.trim()) { toast.error("Enter member name"); return; }
    if (newMember.phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit phone"); return; }
    if (!newMember.sponsor_id.trim()) { toast.error("Enter sponsor ID"); return; }
    setCreatingMember(true);
    try {
      const res = await axios.post(`${API}/franchise/members/create`, {
        name: newMember.name.trim(),
        phone: newMember.phone.replace(/\D/g, ""),
        plan_type: newMember.plan_type,
        sponsor_id: newMember.sponsor_id.trim().toUpperCase(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMemberResult({ ...res.data, sponsor_id: res.data.sponsor_id || newMember.sponsor_id.trim().toUpperCase() });
      // Auto-select the new member for billing
      setSaleType("member");
      setMemberId(res.data.member_id);
      setMemberInfo({ member_id: res.data.member_id, name: res.data.name, phone: res.data.phone, credits: 0, is_active: true, plan_type: res.data.plan_type });
      toast.success(res.data.message || "Member created");
      setNewMember({ name: "", phone: "", plan_type: "D", sponsor_id: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create member");
    } finally {
      setCreatingMember(false);
    }
  };

  const addToCart = (item) => {
    const exists = cartItems.find(c => c.product_id === item.product_id);
    if (exists) {
      if (exists.quantity >= item.available) { toast.error("Max stock reached"); return; }
      setCartItems(cartItems.map(c => c.product_id === item.product_id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCartItems([...cartItems, { product_id: item.product_id, product_name: item.name, quantity: 1, unit_price: item.price, available: item.available, unit: item.unit, point_value: item.point_value || 0 }]);
    }
  };

  const updateCartQuantity = (productId, delta) => {
    setCartItems(cartItems.map(c => {
      if (c.product_id === productId) {
        const newQty = c.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > c.available) { toast.error("Max stock reached"); return c; }
        return { ...c, quantity: newQty };
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => setCartItems(cartItems.filter(c => c.product_id !== productId));

  const getSubtotal = () => cartItems.reduce((sum, c) => sum + c.quantity * c.unit_price, 0);
  const getTotal = () => getSubtotal();
  const getTotalPV = () => cartItems.reduce((sum, c) => sum + c.quantity * (c.point_value || 0), 0);

  const completeSale = async () => {
    if (cartItems.length === 0) { toast.error("Add products to cart"); return; }
    if (saleType === "member" && !memberInfo) { toast.error("Verify member first"); return; }
    if (saleType === "direct" && !customerName) { toast.error("Enter customer name"); return; }

    try {
      const saleData = {
        sale_type: saleType,
        member_id: saleType === "member" ? memberId.toUpperCase() : null,
        customer_name: saleType === "direct" ? customerName : null,
        customer_phone: saleType === "direct" ? customerPhone : null,
        products: cartItems.map(({ product_id, product_name, quantity, unit_price }) => ({ product_id, product_name, quantity, unit_price })),
        payment_mode: paymentMode,
        notes: saleNotes,
        bill_date: billDate
      };

      const res = await axios.post(`${API}/franchise/sales`, saleData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bill created! #${res.data.sale?.bill_number || "Success"}`);
      if (res.data.member_activated) {
        toast.success(`${memberInfo?.member_id || "Member"} activated ✅`, { duration: 5000 });
      }
      
      // Reset form
      setCartItems([]);
      setMemberId("");
      setMemberInfo(null);
      setCustomerName("");
      setCustomerPhone("");
      setSaleNotes("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Sale failed"); }
  };

  const acceptTransfer = async (transferId) => {
    try {
      await axios.post(`${API}/franchise/transfers/${transferId}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Transfer accepted & stock updated");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to accept"); }
  };

  // ---- Franchise own stock (FMCG) ----
  const emptyStockForm = { product_name: "", unit: "kg", pack_label: "", buying_price: "", selling_price: "", quantity: "" };
  const [addStockOpen, setAddStockOpen] = useState(false);
  const [stockForm, setStockForm] = useState(emptyStockForm);
  const [savingStock, setSavingStock] = useState(false);
  const stockProfit = (parseFloat(stockForm.selling_price) || 0) - (parseFloat(stockForm.buying_price) || 0);
  const stockPV = stockProfit > 0 ? +(stockProfit / 5).toFixed(2) : 0;

  const addOwnStock = async () => {
    if (!stockForm.product_name.trim()) { toast.error("Enter product name"); return; }
    const buy = parseFloat(stockForm.buying_price), sell = parseFloat(stockForm.selling_price), qty = parseInt(stockForm.quantity);
    if (isNaN(buy) || isNaN(sell)) { toast.error("Enter buying and selling price"); return; }
    if (sell < buy) { toast.error("Selling price can't be less than buying price"); return; }
    if (isNaN(qty) || qty <= 0) { toast.error("Enter a valid quantity"); return; }
    setSavingStock(true);
    try {
      await axios.post(`${API}/franchise/stock`, {
        product_name: stockForm.product_name.trim(),
        unit: stockForm.unit,
        pack_label: stockForm.pack_label || null,
        buying_price: buy,
        selling_price: sell,
        quantity: qty,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Stock added • ${stockPV} PV/pack`);
      setStockForm(emptyStockForm);
      setAddStockOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setSavingStock(false);
    }
  };

  const deleteOwnStock = async (productId) => {
    try {
      await axios.delete(`${API}/franchise/stock/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Stock item removed");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove"); }
  };

  const filteredStock = stock.filter(s => s.name?.toLowerCase().includes(searchTerm.toLowerCase()) || s.product_name?.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredSales = sales.filter(s => {
    if (historyFilter.date && !s.bill_date?.startsWith(historyFilter.date) && !s.created_at?.startsWith(historyFilter.date)) return false;
    if (historyFilter.type && s.sale_type !== historyFilter.type) return false;
    if (historyFilter.search) {
      const q = historyFilter.search.toLowerCase();
      return (s.member_id?.toLowerCase().includes(q) || s.customer_name?.toLowerCase().includes(q) || s.bill_number?.toLowerCase().includes(q) || s.member_name?.toLowerCase().includes(q));
    }
    return true;
  });

  const tabs = [
    { id: "dashboard", icon: Store, label: L("dashboard", lang) },
    { id: "newSale", icon: Receipt, label: L("newSale", lang) },
    { id: "stock", icon: Package, label: L("stock", lang) },
    { id: "salesHistory", icon: FileText, label: L("salesHistory", lang) },
    { id: "pendingOrders", icon: Truck, label: L("pendingOrders", lang) },
    { id: "orderStock", icon: ShoppingCart, label: L("orderStock", lang) },
    { id: "settlement", icon: DollarSign, label: L("settlement", lang) },
    { id: "dailyReport", icon: TrendingUp, label: L("dailyReport", lang) }
  ];

  if (loading && !franchise) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" data-testid="franchise-dashboard">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Store className="w-6 h-6 text-cyan-600" />
            <span className="font-bold text-lg">{franchise?.name || "Franchise"}</span>
            <span className="hidden sm:inline text-xs bg-cyan-500/20 text-cyan-600 px-2 py-1 rounded-full">{franchise?.franchise_code}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(lang === "en" ? "hi" : "en")} className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200">{lang === "en" ? "हिंदी" : "EN"}</button>
            <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={() => { logout(); navigate("/login"); }} className="p-2 hover:bg-red-500/20 rounded-lg text-red-600"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className={`bg-white/80 border-b border-gray-200 overflow-x-auto ${mobileMenuOpen ? '' : 'hidden md:block'}`}>
        <div className="max-w-7xl mx-auto px-4 flex gap-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-cyan-400 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6" data-testid="franchise-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">₹{(franchise?.total_sales || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">{L("totalSales", lang)}</p>
              </CardContent></Card>
              <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-cyan-600">₹{(franchise?.total_commission || 0).toLocaleString()}</p>
                <p className="text-xs text-gray-500">{L("totalCommission", lang)}</p>
              </CardContent></Card>
              <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                <Receipt className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-600">{sales.length}</p>
                <p className="text-xs text-gray-500">Total Bills</p>
              </CardContent></Card>
              <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                <Package className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-600">{stock.length}</p>
                <p className="text-xs text-gray-500">Products in Stock</p>
              </CardContent></Card>
            </div>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button onClick={() => setActiveTab("newSale")} className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white py-6 text-lg" data-testid="quick-new-bill">
                <Receipt className="w-5 h-5 mr-2" /> {L("newSale", lang)}
              </Button>
              <Button onClick={() => setActiveTab("salesHistory")} variant="outline" className="border-gray-300 text-gray-900 py-6 text-lg" data-testid="quick-history">
                <FileText className="w-5 h-5 mr-2" /> {L("salesHistory", lang)}
              </Button>
            </div>

            {/* Today's Summary */}
            {dailyReport && (
              <Card className="bg-white border-gray-200">
                <CardHeader><CardTitle className="text-gray-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-cyan-600" /> Today&apos;s Summary</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-3 gap-4">
                  <div className="text-center"><p className="text-2xl font-bold text-gray-900">{dailyReport.total_sales || 0}</p><p className="text-xs text-gray-500">Bills</p></div>
                  <div className="text-center"><p className="text-2xl font-bold text-green-600">₹{(dailyReport.total_amount || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Revenue</p></div>
                  <div className="text-center"><p className="text-2xl font-bold text-cyan-600">₹{(dailyReport.total_commission || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Commission</p></div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* New Billing Tab */}
        {activeTab === "newSale" && (
          <div className="space-y-4" data-testid="new-bill-section">
            <h2 className="text-xl font-bold flex items-center gap-2"><Receipt className="w-5 h-5 text-cyan-600" /> {L("newSale", lang)}</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: Sale Type & Customer Info */}
              <div className="space-y-4">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-4 space-y-3">
                    <Label className="text-gray-700">Sale Type</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button onClick={() => { setSaleType("direct"); setMemberInfo(null); }} variant={saleType === "direct" ? "default" : "outline"} className={saleType === "direct" ? "bg-cyan-500" : "border-gray-300 text-gray-900"} data-testid="sale-type-direct">
                        <User className="w-4 h-4 mr-1" /> {L("directSale", lang)}
                      </Button>
                      <Button onClick={() => setSaleType("member")} variant={saleType === "member" ? "default" : "outline"} className={saleType === "member" ? "bg-purple-500" : "border-gray-300 text-gray-900"} data-testid="sale-type-member">
                        <Users className="w-4 h-4 mr-1" /> {L("memberSale", lang)}
                      </Button>
                    </div>
                    
                    {saleType === "direct" ? (
                      <div className="space-y-2">
                        <div><Label className="text-gray-500 text-xs">{L("customerName", lang)} *</Label>
                        <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer Name" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="customer-name-input" /></div>
                        <div><Label className="text-gray-500 text-xs">{L("phone", lang)}</Label>
                        <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone (Optional)" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="customer-phone-input" /></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-gray-500 text-xs">{L("memberId", lang)}</Label>
                        <div className="flex gap-2">
                          <Input value={memberId} onChange={e => setMemberId(e.target.value)} placeholder="ACJ00001" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="member-id-input" />
                          <Button onClick={verifyMember} className="bg-purple-500 hover:bg-purple-600" data-testid="verify-member-btn">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button variant="outline" onClick={() => { setNewMemberResult(null); setNewMemberOpen(true); }}
                          className="w-full border-dashed border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="open-create-member-btn">
                          <Plus className="w-4 h-4 mr-1" /> Create New Member ID
                        </Button>
                        <p className="text-[11px] text-gray-400">No ID? Create one here — it's activated instantly so you can bill.</p>
                        {memberInfo && (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-sm">
                            <p className="text-green-600 font-medium">{memberInfo.name}</p>
                            <p className="text-gray-500 text-xs">{memberInfo.member_id} | Credits: ₹{memberInfo.credits || 0}</p>
                            {memberInfo.is_active === false && (
                              <p className="text-orange-600 text-xs font-semibold mt-1" data-testid="member-inactive-note">
                                ⚠ Inactive — creating this bill will activate the member.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div><Label className="text-gray-500 text-xs">Bill Date</Label>
                    <Input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} className="bg-gray-100 border-gray-300 text-gray-900" data-testid="bill-date-input" /></div>
                    
                    <div><Label className="text-gray-500 text-xs">{L("paymentMode", lang)}</Label>
                    <div className="grid grid-cols-3 gap-1">
                      {["cash", "upi", "wallet"].map(mode => (
                        <Button key={mode} size="sm" onClick={() => setPaymentMode(mode)} variant={paymentMode === mode ? "default" : "outline"}
                          className={`text-xs ${paymentMode === mode ? 'bg-cyan-500' : 'border-gray-300 text-gray-900'}`} data-testid={`payment-${mode}`}>
                          {mode === "cash" && <Banknote className="w-3 h-3 mr-1" />}
                          {mode === "upi" && <Smartphone className="w-3 h-3 mr-1" />}
                          {mode === "wallet" && <CreditCard className="w-3 h-3 mr-1" />}
                          {L(mode, lang)}
                        </Button>
                      ))}
                    </div>
                    {paymentMode === "wallet" && memberInfo && (
                      <div className="mt-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg p-2 text-sm" data-testid="wallet-pay-info">
                        <p className="text-gray-700">Member wallet balance: <span className="font-bold text-[#B5952F]">₹{memberInfo.credits || 0}</span></p>
                        <p className="text-gray-500 text-xs">This amount will be deducted from the member's wallet credits.</p>
                      </div>
                    )}
                    {paymentMode === "upi" && (
                      <div className="mt-3 bg-white rounded-xl p-3 flex flex-col items-center" data-testid="upi-qr-block">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Scan to pay via any UPI app</p>
                        <img src="/upi-qr.jpeg" alt="UPI QR Code" className="w-48 h-auto rounded-lg" data-testid="upi-qr-image" />
                        <p className="text-[11px] text-gray-500 mt-2">UPI ID: 7980921094@kotak811</p>
                      </div>
                    )}
                    </div>
                    
                    <div><Label className="text-gray-500 text-xs">Notes</Label>
                    <Input value={saleNotes} onChange={e => setSaleNotes(e.target.value)} placeholder="Optional notes..." className="bg-gray-100 border-gray-300 text-gray-900" /></div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Middle: Product Search & Add */}
              <div className="space-y-4">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-4 space-y-3">
                    <Label className="text-gray-700">{L("selectProducts", lang)}</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
                      <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search products..." className="bg-gray-100 border-gray-300 text-gray-900 pl-9" data-testid="product-search-input" />
                    </div>
                    <div className="max-h-[320px] overflow-y-auto space-y-1">
                      {filteredStock.length > 0 ? filteredStock.map(item => (
                        <div key={item.product_id} className="flex items-center justify-between p-2 bg-gray-100/50 rounded-lg border border-gray-200 hover:border-cyan-500/30 transition-colors">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.name || item.product_name}</p>
                            <p className="text-xs text-gray-500">₹{item.unit_price || item.price} / {item.unit || 'unit'} | Stock: {item.quantity}{item.point_value > 0 ? <span className="text-purple-600 font-semibold"> | {item.point_value} PV</span> : null}</p>
                          </div>
                          <Button size="sm" onClick={() => addToCart({ product_id: item.product_id, name: item.product_name || item.name, price: item.unit_price || item.price || 0, available: item.quantity, unit: item.unit, point_value: item.point_value })}
                            className="bg-cyan-500/20 text-cyan-600 hover:bg-cyan-500/30 ml-2" disabled={item.quantity <= 0} data-testid={`add-product-${item.product_id}`}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      )) : (
                        <div className="text-center py-8 text-gray-500"><Package className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">{searchTerm ? "No products match" : L("noStock", lang)}</p></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Right: Cart & Bill Summary */}
              <div className="space-y-4">
                <Card className="bg-white border-gray-200">
                  <CardContent className="p-4 space-y-3">
                    <Label className="text-gray-700 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Cart ({cartItems.length})</Label>
                    
                    {cartItems.length > 0 ? (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {cartItems.map(item => (
                          <div key={item.product_id} className="flex items-center justify-between p-2 bg-gray-100/50 rounded-lg border border-gray-200">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-900 truncate">{item.product_name}</p>
                              <p className="text-xs text-gray-500">₹{item.unit_price} x {item.quantity} = ₹{item.unit_price * item.quantity}{item.point_value > 0 ? <span className="text-purple-600"> | {item.point_value * item.quantity} PV</span> : null}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <button onClick={() => updateCartQuantity(item.product_id, -1)} className="p-1 bg-gray-200 rounded hover:bg-gray-200"><Minus className="w-3 h-3" /></button>
                              <span className="text-sm w-6 text-center">{item.quantity}</span>
                              <button onClick={() => updateCartQuantity(item.product_id, 1)} className="p-1 bg-gray-200 rounded hover:bg-gray-200"><Plus className="w-3 h-3" /></button>
                              <button onClick={() => removeFromCart(item.product_id)} className="p-1 text-red-600 hover:bg-red-500/20 rounded ml-1"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500"><ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Cart is empty</p></div>
                    )}
                    
                    {/* Bill Calculation */}
                    {cartItems.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-gray-200">
                        <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="text-gray-900">₹{getSubtotal().toLocaleString()}</span></div>
                        <div className="flex justify-between text-lg font-bold pt-1 border-t border-gray-300"><span className="text-cyan-600">Total (Business Volume)</span><span className="text-green-600">₹{getTotal().toLocaleString()}</span></div>
                        <div className="flex justify-between text-sm font-semibold bg-purple-500/10 rounded-lg px-2 py-1.5"><span className="text-purple-600">Total Point Value</span><span className="text-purple-600" data-testid="cart-total-pv">{getTotalPV()} PV</span></div>
                      </div>
                    )}
                    
                    <Button onClick={completeSale} className="w-full bg-gradient-to-r from-green-500 to-cyan-500 text-white font-bold py-3" disabled={cartItems.length === 0} data-testid="complete-sale-btn">
                      <Receipt className="w-4 h-4 mr-2" /> {L("completeSale", lang)}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Stock Tab */}
        {activeTab === "stock" && (
          <div className="space-y-4" data-testid="stock-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-xl font-bold flex items-center gap-2"><Package className="w-5 h-5 text-amber-600" /> {L("stock", lang)}</h2>
              <Button onClick={() => { setStockForm(emptyStockForm); setAddStockOpen(true); }} className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white" data-testid="open-add-stock-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Stock
              </Button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search stock..." className="bg-gray-100 border-gray-300 text-gray-900 pl-9 max-w-sm" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredStock.map(item => (
                <Card key={item.product_id} className="bg-white border-gray-200" data-testid={`stock-card-${item.product_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-gray-900 truncate">
                        {item.name || item.product_name}
                        {item.pack_label ? <span className="text-gray-400 text-xs font-normal"> ({item.pack_label})</span> : null}
                      </h3>
                      {item.is_own_stock && (
                        <button onClick={() => deleteOwnStock(item.product_id)} className="text-red-500 hover:text-red-600 shrink-0" title="Remove" data-testid={`delete-stock-${item.product_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-500 text-sm">₹{item.unit_price || item.selling_price || item.price} / {item.unit || 'unit'}</span>
                      <span className={`font-bold ${item.quantity > 5 ? 'text-green-600' : item.quantity > 0 ? 'text-amber-600' : 'text-red-600'}`}>{item.quantity} {item.unit}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {item.point_value > 0 && <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded">{item.point_value} PV</span>}
                      {item.is_own_stock && item.profit_per_unit != null && <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded">₹{item.profit_per_unit} profit</span>}
                      {item.is_own_stock ? <span className="text-[10px] text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded">Own stock</span> : <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">HO stock</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Sales History Tab */}
        {activeTab === "salesHistory" && (
          <div className="space-y-4" data-testid="sales-history-section">
            <h2 className="text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-purple-600" /> {L("salesHistory", lang)}</h2>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <Input type="date" value={historyFilter.date} onChange={e => setHistoryFilter({...historyFilter, date: e.target.value})} className="bg-gray-100 border-gray-300 text-gray-900 w-40" data-testid="history-date-filter" />
              <Input value={historyFilter.search} onChange={e => setHistoryFilter({...historyFilter, search: e.target.value})} placeholder="Search bill #, member name or ID..." className="bg-gray-100 border-gray-300 text-gray-900 w-52" data-testid="history-search-filter" />
              <select value={historyFilter.type} onChange={e => setHistoryFilter({...historyFilter, type: e.target.value})} className="bg-gray-100 border border-gray-300 text-gray-900 rounded-md px-3 py-2 text-sm" data-testid="history-type-filter">
                <option value="">All Types</option>
                <option value="direct">Direct</option>
                <option value="member">Member</option>
              </select>
              {(historyFilter.date || historyFilter.search || historyFilter.type) && (
                <Button size="sm" variant="outline" className="border-gray-300 text-gray-500" onClick={() => setHistoryFilter({ date: "", search: "", type: "" })}>
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              )}
            </div>
            
            <p className="text-sm text-gray-500">{filteredSales.length} bill(s) found</p>
            
            <div className="space-y-2">
              {filteredSales.length > 0 ? filteredSales.map(sale => (
                <Card key={sale.id} onClick={() => setSelectedBill(sale)} className="bg-white border-gray-200 hover:border-cyan-500/50 transition-colors cursor-pointer" data-testid={`bill-card-${sale.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-cyan-600">{sale.bill_number || "—"}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${sale.sale_type === 'member' ? 'bg-purple-500/20 text-purple-600' : 'bg-cyan-500/20 text-cyan-600'}`}>{sale.sale_type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${sale.payment_status === 'completed' ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600'}`}>{sale.payment_status}</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{sale.member_name || sale.customer_name || "—"} {sale.member_id ? `(${sale.member_id})` : ""}</p>
                        <p className="text-xs text-gray-500">{sale.bill_date || sale.created_at?.split("T")[0]} | {sale.payment_mode} | {sale.products?.length} item(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">₹{(sale.total_amount || 0).toLocaleString()}</p>
                        {sale.total_pv > 0 && <p className="text-xs text-purple-600 font-semibold">{sale.total_pv} PV</p>}
                        <p className="text-xs text-gray-500">Comm: ₹{(sale.commission_amount || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <div className="text-center py-12 text-gray-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No bills found</p></div>
              )}
            </div>
          </div>
        )}

        {/* Pending Orders Tab */}
        {activeTab === "pendingOrders" && (
          <div className="space-y-4" data-testid="pending-orders-section">
            <h2 className="text-xl font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-amber-600" /> {L("pendingOrders", lang)}</h2>
            {pendingTransfers.length > 0 ? pendingTransfers.map(t => {
              const items = t.products || [];
              const totalQty = items.reduce((s, p) => s + (p.quantity || 0), 0);
              return (
              <Card key={t.id} className="bg-white border-gray-200">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-gray-900 font-medium truncate">
                      {items.length > 0 ? items.map(p => `${p.product_name} (x${p.quantity})`).join(", ") : (t.product_name || "Stock Transfer")}
                    </p>
                    <p className="text-sm text-gray-500">{items.length} item(s) | Total Qty: {totalQty || t.quantity || 0} | {t.created_at?.split("T")[0]}</p>
                  </div>
                  <Button onClick={() => acceptTransfer(t.id)} className="bg-green-500 hover:bg-green-600 shrink-0" data-testid={`accept-transfer-${t.id}`}>
                    <CheckCircle className="w-4 h-4 mr-1" /> Accept
                  </Button>
                </CardContent>
              </Card>
              );
            }) : (
              <div className="text-center py-12 text-gray-500"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No pending transfers</p></div>
            )}
          </div>
        )}

        {/* Order Stock Tab (Franchise -> Head Office) */}
        {activeTab === "orderStock" && (
          <div className="space-y-4" data-testid="order-stock-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-cyan-600" /> {L("orderStock", lang)}</h2>
              <span className="text-xs text-amber-600 bg-amber-500/10 px-3 py-1 rounded-full">Min order: ₹{MIN_ORDER_VALUE.toLocaleString()}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Catalog */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-gray-900 text-base">Head Office Catalog</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="Search products..." className="bg-gray-100 border-gray-300 text-gray-900 pl-9" data-testid="order-product-search" />
                  </div>
                  <div className="space-y-1 max-h-[420px] overflow-y-auto">
                    {catalog.filter(p => p.name?.toLowerCase().includes(orderSearch.toLowerCase())).map(p => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-gray-100/50 rounded border border-gray-200">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                          <p className="text-xs text-gray-500">₹{p.price}{p.is_internal ? <span className="text-purple-600"> · internal</span> : null}</p>
                        </div>
                        <Button size="sm" onClick={() => addToOrder(p)} className="bg-cyan-500 hover:bg-cyan-600 shrink-0" data-testid={`order-add-${p.id}`}><Plus className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    {catalog.length === 0 && <p className="text-center text-gray-500 py-6 text-sm">Loading catalog...</p>}
                  </div>
                </CardContent>
              </Card>

              {/* Order Cart */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="pb-2"><CardTitle className="text-gray-900 text-base">Your Order</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {orderCart.length === 0 ? (
                    <p className="text-center text-gray-500 py-6 text-sm">Add products from the catalog to build your order</p>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-[280px] overflow-y-auto">
                        {orderCart.map(c => (
                          <div key={c.product_id} className="flex items-center gap-2 p-2 bg-gray-100/50 rounded">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-900 truncate">{c.product_name}</p>
                              <p className="text-xs text-gray-500">₹{c.unit_price} x {c.quantity} = ₹{(c.unit_price * c.quantity).toLocaleString()}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateOrderQty(c.product_id, c.quantity - 1)} className="w-7 h-7 rounded bg-gray-200 text-gray-900 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                              <input type="number" value={c.quantity} onChange={e => updateOrderQty(c.product_id, parseInt(e.target.value) || 0)} className="w-12 text-center bg-gray-100 border border-gray-300 rounded text-gray-900 text-sm py-1" data-testid={`order-qty-${c.product_id}`} />
                              <button onClick={() => updateOrderQty(c.product_id, c.quantity + 1)} className="w-7 h-7 rounded bg-gray-200 text-gray-900 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                              <button onClick={() => updateOrderQty(c.product_id, 0)} className="text-red-600 hover:text-red-600 ml-1"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Input value={orderNotes} onChange={e => setOrderNotes(e.target.value)} placeholder="Notes (optional)" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="order-notes" />
                      <div className="border-t border-gray-200 pt-3 space-y-2">
                        <div className="flex justify-between text-lg font-bold"><span className="text-cyan-600">Order Total</span><span className={getOrderTotal() >= MIN_ORDER_VALUE ? "text-green-600" : "text-amber-600"} data-testid="order-total">₹{getOrderTotal().toLocaleString()}</span></div>
                        {getOrderTotal() < MIN_ORDER_VALUE && orderCart.length > 0 && (
                          <p className="text-xs text-amber-600">Add ₹{(MIN_ORDER_VALUE - getOrderTotal()).toLocaleString()} more to reach the ₹{MIN_ORDER_VALUE.toLocaleString()} minimum.</p>
                        )}
                        <Button onClick={placeOrder} disabled={placingOrder || getOrderTotal() < MIN_ORDER_VALUE} className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white disabled:opacity-50" data-testid="place-order-btn">
                          {placingOrder ? "Placing..." : "Place Order to Head Office"}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* My Orders */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700 mt-4">My Orders</h3>
              {myOrders.length > 0 ? myOrders.map(o => (
                <Card key={o.id} className="bg-white border-gray-200" data-testid={`order-row-${o.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-cyan-600">{o.order_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'approved' ? 'bg-green-500/20 text-green-600' : o.status === 'rejected' ? 'bg-red-500/20 text-red-600' : o.status === 'fulfilled' ? 'bg-purple-500/20 text-purple-600' : 'bg-amber-500/20 text-amber-600'}`}>{o.status}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{o.products?.length} item(s) | {o.created_at?.split("T")[0]}</p>
                      {o.admin_notes && <p className="text-xs text-gray-500 italic mt-0.5">HO: {o.admin_notes}</p>}
                    </div>
                    <p className="text-lg font-bold text-green-600">₹{(o.total_value || 0).toLocaleString()}</p>
                  </CardContent>
                </Card>
              )) : (
                <div className="text-center py-8 text-gray-500"><ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">No orders yet</p></div>
              )}
            </div>
          </div>
        )}

        {/* Settlement Tab (Weekly commission settlement) */}
        {activeTab === "settlement" && (
          <div className="space-y-4" data-testid="settlement-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-xl font-bold flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" /> Weekly Settlement</h2>
              {settlement && <span className="text-xs text-cyan-600 bg-cyan-500/10 px-3 py-1 rounded-full">Next settlement: Sat {settlement.next_settlement_date}</span>}
            </div>

            {settlement ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Card className="bg-white border-gray-200"><CardContent className="p-4">
                    <p className="text-xs text-gray-500">Cash in Hand</p>
                    <p className="text-2xl font-bold text-amber-600" data-testid="settle-cash">₹{settlement.cash_collected.toLocaleString()}</p>
                    <p className="text-[11px] text-gray-500">held by you (cash sales)</p>
                  </CardContent></Card>
                  <Card className="bg-white border-gray-200"><CardContent className="p-4">
                    <p className="text-xs text-gray-500">UPI → Head Office</p>
                    <p className="text-2xl font-bold text-cyan-600" data-testid="settle-upi">₹{settlement.upi_collected.toLocaleString()}</p>
                    <p className="text-[11px] text-gray-500">received directly by HO</p>
                  </CardContent></Card>
                  <Card className="bg-white border-gray-200"><CardContent className="p-4">
                    <p className="text-xs text-gray-500">Card → Head Office</p>
                    <p className="text-2xl font-bold text-cyan-600">₹{settlement.card_collected.toLocaleString()}</p>
                    <p className="text-[11px] text-gray-500">received by HO</p>
                  </CardContent></Card>
                  <Card className="bg-white border-gray-200"><CardContent className="p-4">
                    <p className="text-xs text-gray-500">Commission Earned</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="settle-commission">₹{settlement.total_commission.toLocaleString()}</p>
                    <p className="text-[11px] text-gray-500">this cycle ({settlement.unsettled_count} bills)</p>
                  </CardContent></Card>
                </div>

                {/* Net settlement banner */}
                <Card className={settlement.direction === "franchise_to_ho" ? "bg-amber-500/10 border-amber-500/40" : "bg-green-500/10 border-green-500/40"}>
                  <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3" data-testid="settle-net">
                    <div>
                      {settlement.direction === "franchise_to_ho" ? (
                        <>
                          <p className="text-sm text-amber-600 font-semibold">You owe Head Office (remit by Saturday)</p>
                          <p className="text-xs text-gray-500">Cash in hand ₹{settlement.cash_collected.toLocaleString()} − Commission ₹{settlement.total_commission.toLocaleString()}. Keep your commission from the cash and remit the balance.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-green-600 font-semibold">Head Office will pay you (on Sunday)</p>
                          <p className="text-xs text-gray-500">Commission ₹{settlement.total_commission.toLocaleString()} − Cash in hand ₹{settlement.cash_collected.toLocaleString()}. Cash isn&apos;t enough to cover your commission, so HO pays the difference.</p>
                        </>
                      )}
                    </div>
                    <p className={`text-3xl font-extrabold ${settlement.direction === "franchise_to_ho" ? "text-amber-600" : "text-green-600"}`}>₹{settlement.net_amount.toLocaleString()}</p>
                  </CardContent>
                </Card>

                {/* History */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 mt-4">Settlement History</h3>
                  {settlementHistory.length > 0 ? settlementHistory.map(s => (
                    <Card key={s.id} className="bg-white border-gray-200">
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm text-gray-900">{s.settled_at?.split("T")[0]} <span className="text-xs text-green-600 ml-1">settled</span></p>
                          <p className="text-xs text-gray-500">Cash ₹{(s.cash_collected||0).toLocaleString()} | Commission ₹{(s.total_commission||0).toLocaleString()} | {s.unsettled_count} bills</p>
                          {s.notes && <p className="text-xs text-gray-500 italic">{s.notes}</p>}
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${s.direction === "franchise_to_ho" ? "text-amber-600" : "text-green-600"}`}>₹{(s.net_amount||0).toLocaleString()}</p>
                          <p className="text-[11px] text-gray-500">{s.direction === "franchise_to_ho" ? "paid to HO" : "received from HO"}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )) : (
                    <div className="text-center py-8 text-gray-500"><DollarSign className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="text-sm">No settlements yet</p></div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500"><RefreshCw className="w-10 h-10 mx-auto mb-2 opacity-50 animate-spin" /><p className="text-sm">Loading settlement...</p></div>
            )}
          </div>
        )}

        {/* Daily Report Tab */}
        {activeTab === "dailyReport" && (
          <div className="space-y-4" data-testid="daily-report-section">
            <h2 className="text-xl font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> {L("dailyReport", lang)}</h2>
            {dailyReport ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-gray-900">{dailyReport.total_sales || 0}</p>
                    <p className="text-xs text-gray-500">Bills Today</p>
                  </CardContent></Card>
                  <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">₹{(dailyReport.total_amount || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total Revenue</p>
                  </CardContent></Card>
                  <Card className="bg-white border-gray-200"><CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-cyan-600">₹{(dailyReport.total_commission || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Commission</p>
                  </CardContent></Card>
                </div>
                {dailyReport.sales && dailyReport.sales.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-700">Today&apos;s Bills</h3>
                    {dailyReport.sales.map((sale, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div>
                          <span className="text-sm text-cyan-600 font-mono">{sale.bill_number || `#${idx+1}`}</span>
                          <span className="text-sm text-gray-700 ml-2">{sale.member_name || sale.customer_name}</span>
                        </div>
                        <span className="font-bold text-green-600">₹{sale.total_amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No sales today</p></div>
            )}
          </div>
        )}
      </main>

      {/* Add Own Stock Modal */}
      <Dialog open={addStockOpen} onOpenChange={setAddStockOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="add-stock-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-600">
              <Package className="w-5 h-5" /> Add Stock to Store
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-gray-500 text-xs">Product Name *</Label>
              <Input value={stockForm.product_name} onChange={e => setStockForm({ ...stockForm, product_name: e.target.value })} placeholder="e.g. Aashirvaad Atta" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="stock-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-gray-500 text-xs">Unit *</Label>
                <select value={stockForm.unit} onChange={e => setStockForm({ ...stockForm, unit: e.target.value })} className="w-full bg-gray-100 border border-gray-300 text-gray-900 rounded-md px-3 py-2 text-sm" data-testid="stock-unit-select">
                  <option value="kg">Kilogram (kg)</option>
                  <option value="ltr">Litre (ltr)</option>
                  <option value="pcs">Pieces (pcs)</option>
                  <option value="gram">Gram (g)</option>
                  <option value="ml">Millilitre (ml)</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Pack Label</Label>
                <Input value={stockForm.pack_label} onChange={e => setStockForm({ ...stockForm, pack_label: e.target.value })} placeholder="e.g. 5kg" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="stock-pack-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-gray-500 text-xs">Buying Price (₹) *</Label>
                <Input type="number" value={stockForm.buying_price} onChange={e => setStockForm({ ...stockForm, buying_price: e.target.value })} placeholder="150" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="stock-buy-input" />
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Selling Price (₹) *</Label>
                <Input type="number" value={stockForm.selling_price} onChange={e => setStockForm({ ...stockForm, selling_price: e.target.value })} placeholder="175" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="stock-sell-input" />
              </div>
            </div>
            <div>
              <Label className="text-gray-500 text-xs">Quantity (packs) *</Label>
              <Input type="number" value={stockForm.quantity} onChange={e => setStockForm({ ...stockForm, quantity: e.target.value })} placeholder="10" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="stock-qty-input" />
            </div>
            {/* Live profit + PV */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center justify-around text-center" data-testid="stock-pv-preview">
              <div>
                <p className="text-[11px] text-gray-500">Profit / pack</p>
                <p className={`font-bold ${stockProfit >= 0 ? "text-green-600" : "text-red-600"}`}>₹{stockProfit.toFixed(2)}</p>
              </div>
              <div className="h-8 w-px bg-purple-200" />
              <div>
                <p className="text-[11px] text-gray-500">PV / pack (÷5)</p>
                <p className="font-bold text-purple-600" data-testid="stock-pv-value">{stockPV} PV</p>
              </div>
            </div>
            <Button className="w-full bg-cyan-500 hover:bg-cyan-600 text-white" onClick={addOwnStock} disabled={savingStock} data-testid="submit-add-stock-btn">
              {savingStock ? "Adding..." : "Add to Stock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Create New Member Modal (form only) */}
      <Dialog open={newMemberOpen && !newMemberResult} onOpenChange={(open) => { setNewMemberOpen(open); if (!open) setNewMemberResult(null); }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="create-member-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Plus className="w-5 h-5" /> Create New Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
              <div>
                <Label className="text-gray-500 text-xs">Full Name *</Label>
                <Input value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} placeholder="Member name" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="new-member-name" />
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Phone (10 digits) *</Label>
                <Input value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="new-member-phone" />
                <p className="text-[11px] text-gray-400 mt-0.5">Login password = last 4 digits of phone.</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Sponsor ID *</Label>
                <Input value={newMember.sponsor_id} onChange={e => setNewMember({ ...newMember, sponsor_id: e.target.value.toUpperCase() })} placeholder="ACJ00001" className="bg-gray-100 border-gray-300 text-gray-900" data-testid="new-member-sponsor" />
              </div>
              <div>
                <Label className="text-gray-500 text-xs">Plan *</Label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {["D", "C", "B", "A"].map(p => (
                    <Button key={p} type="button" size="sm" variant={newMember.plan_type === p ? "default" : "outline"}
                      onClick={() => setNewMember({ ...newMember, plan_type: p })}
                      className={newMember.plan_type === p ? "bg-purple-500 text-white" : "border-gray-300 text-gray-900"}
                      data-testid={`new-member-plan-${p}`}>
                      {p}
                    </Button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">D=₹500, C=₹1000, B=₹2000, A=₹4000. No wallet credits are added on franchise creation.</p>
              </div>
              <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white" onClick={createMember} disabled={creatingMember} data-testid="submit-create-member-btn">
                {creatingMember ? "Creating..." : "Create & Activate Member"}
              </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-screen Member ID Card (shown after successful creation) */}
      {newMemberResult && (
        <div className="fixed inset-0 z-[120] flex flex-col items-center justify-center p-4 overflow-y-auto"
          style={{ background: "radial-gradient(circle at 50% 0%, #2a1a4a 0%, #14102b 45%, #0a0715 100%)" }}
          data-testid="new-member-idcard">
          <button
            onClick={() => setNewMemberResult(null)}
            className="absolute top-4 right-4 text-white/60 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="new-member-close-btn" aria-label="Close">
            <X className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300 font-semibold text-xs tracking-[0.2em]">MEMBER CREATED &amp; ACTIVATED</p>
          </div>

          {/* Premium ID Card */}
          <div className="relative w-full max-w-sm rounded-[28px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            style={{ background: "linear-gradient(160deg, #1c1633 0%, #241a44 50%, #14102b 100%)", border: "1px solid rgba(212,175,55,0.45)" }}>
            {/* subtle gold glow accents */}
            <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.28), transparent 70%)" }}></div>
            <div className="pointer-events-none absolute -bottom-20 -left-16 w-56 h-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(124,58,237,0.25), transparent 70%)" }}></div>

            {/* Gold header band with logo */}
            <div className="relative flex items-center gap-3 px-5 py-4"
              style={{ background: "linear-gradient(90deg, #8a6d1f 0%, #D4AF37 55%, #f0d879 100%)" }}>
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-md ring-2 ring-white/70 shrink-0">
                <img src="/achraj-logo.png" alt="Achraj" className="w-11 h-11 object-contain rounded-full" />
              </div>
              <div className="leading-tight">
                <p className="text-[#2a1a05] font-extrabold text-[15px]">Achraj Services Pvt. Ltd.</p>
                <p className="text-[#4a3510] text-[10px] font-semibold tracking-[0.18em]">MEMBER IDENTITY CARD</p>
              </div>
            </div>

            {/* Body */}
            <div className="relative px-6 pt-6 pb-5 flex flex-col items-center text-center">
              {/* Avatar with gold ring */}
              <div className="w-24 h-24 rounded-full p-[3px]" style={{ background: "linear-gradient(135deg, #D4AF37, #f0d879)" }}>
                <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <span className="text-white text-4xl font-bold">{(newMemberResult.name || "?").charAt(0).toUpperCase()}</span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-white mt-3" data-testid="idcard-name">{newMemberResult.name}</h3>
              <p className="text-2xl font-extrabold tracking-[0.15em] mt-1"
                style={{ color: "#f0d879" }} data-testid="idcard-member-id">{newMemberResult.member_id}</p>

              <div className="flex items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-xs font-semibold px-3 py-1 rounded-full">
                  <CheckCircle className="w-3.5 h-3.5" /> Active
                </span>
                {newMemberResult.plan_type && (
                  <span className="bg-purple-500/15 text-purple-300 border border-purple-500/30 text-xs font-semibold px-3 py-1 rounded-full">Plan {newMemberResult.plan_type}</span>
                )}
              </div>

              {/* Detail rows */}
              <div className="w-full mt-5 space-y-2 text-left">
                {newMemberResult.sponsor_id && (
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-2.5" data-testid="idcard-sponsor">
                    <span className="text-white/50 text-xs flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Sponsor ID</span>
                    <span className="text-white font-semibold text-sm tracking-wide">
                      {newMemberResult.sponsor_id}
                      {newMemberResult.sponsor_name ? <span className="text-white/40 font-normal ml-1">({newMemberResult.sponsor_name})</span> : null}
                    </span>
                  </div>
                )}

                {/* Password hint (no actual password shown) */}
                <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-400/25 rounded-xl px-4 py-3">
                  <Smartphone className="w-5 h-5 text-blue-300 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-200">Login Password</p>
                    <p className="text-xs text-blue-300/80 mt-0.5">Use the <strong>last 4 digits of your phone number</strong> as your password.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer strip */}
            <div className="relative border-t border-white/10 px-6 py-2 text-center bg-black/20">
              <p className="text-[10px] text-[#D4AF37]/70 tracking-[0.2em]">ACHRAJAI.COM • अपना खर्च नहीं, आय करें</p>
            </div>
          </div>

          <Button
            className="w-full max-w-sm mt-6 text-black font-semibold h-12 shadow-lg"
            style={{ background: "linear-gradient(90deg, #D4AF37, #f0d879)" }}
            onClick={() => { setNewMemberOpen(false); setNewMemberResult(null); }}
            data-testid="new-member-done-btn">
            Continue to Billing
          </Button>
        </div>
      )}


      {/* Bill Detail Modal */}
      <Dialog open={!!selectedBill} onOpenChange={(open) => !open && setSelectedBill(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="bill-detail-modal">
          {selectedBill && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-gray-900">
                  <Receipt className="w-5 h-5 text-cyan-600" /> Bill Details
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Bill meta */}
                <div className="bg-gray-100/60 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Bill Number</span>
                    <span className="font-mono text-sm text-cyan-600" data-testid="bill-detail-number">{selectedBill.bill_number || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Date</span>
                    <span className="text-sm text-gray-900" data-testid="bill-detail-date">{selectedBill.bill_date || selectedBill.created_at?.split("T")[0] || "—"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Type</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedBill.sale_type === 'member' ? 'bg-purple-500/20 text-purple-600' : 'bg-cyan-500/20 text-cyan-600'}`}>{selectedBill.sale_type}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Status</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedBill.payment_status === 'completed' ? 'bg-green-500/20 text-green-600' : 'bg-amber-500/20 text-amber-600'}`}>{selectedBill.payment_status}</span>
                  </div>
                </div>

                {/* Customer / Member */}
                <div className="bg-gray-100/60 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{selectedBill.sale_type === 'member' ? 'Member Name' : 'Customer Name'}</span>
                    <span className="text-sm text-gray-900" data-testid="bill-detail-name">{selectedBill.member_name || selectedBill.customer_name || "—"}</span>
                  </div>
                  {selectedBill.member_id && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Member ID</span>
                      <span className="font-mono text-sm text-purple-600" data-testid="bill-detail-member-id">{selectedBill.member_id}</span>
                    </div>
                  )}
                  {selectedBill.customer_phone && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Phone</span>
                      <span className="text-sm text-gray-900">{selectedBill.customer_phone}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Payment Mode</span>
                    <span className="text-sm text-gray-900 capitalize">{selectedBill.payment_mode || "—"}</span>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Items ({selectedBill.products?.length || 0})</p>
                  <div className="space-y-1 max-h-[180px] overflow-y-auto">
                    {(selectedBill.products || []).map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-100/40 rounded p-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 truncate">{p.product_name}</p>
                          <p className="text-xs text-gray-500">₹{p.unit_price} x {p.quantity}{p.point_value > 0 ? <span className="text-purple-600"> | {(p.point_value || 0) * p.quantity} PV</span> : null}</p>
                        </div>
                        <span className="text-sm font-semibold text-green-600 ml-2">₹{((p.unit_price || 0) * (p.quantity || 0)).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t border-gray-200 pt-3 space-y-1.5">
                  {selectedBill.discount > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Discount</span><span className="text-red-600">-₹{selectedBill.discount}</span></div>
                  )}
                  {selectedBill.gst_amount > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-gray-500">GST ({selectedBill.gst_percent}%)</span><span className="text-gray-900">₹{selectedBill.gst_amount}</span></div>
                  )}
                  <div className="flex justify-between text-base font-bold"><span className="text-cyan-600">Total (Business Volume)</span><span className="text-green-600">₹{(selectedBill.total_amount || 0).toLocaleString()}</span></div>
                  {selectedBill.total_pv > 0 && (
                    <div className="flex justify-between text-sm font-semibold"><span className="text-purple-600">Total Point Value</span><span className="text-purple-600">{selectedBill.total_pv} PV</span></div>
                  )}
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Commission ({selectedBill.commission_percent || 0}%)</span><span className="text-amber-600">₹{(selectedBill.commission_amount || 0).toLocaleString()}</span></div>
                </div>

                {selectedBill.notes && (
                  <p className="text-xs text-gray-500 italic">Note: {selectedBill.notes}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
